import { getSession } from "@/stores/session";
import { allRoute } from "@/router/routes";

/**
 * An administrator holds every permission, including ones introduced by modules
 * released after their account was created. The API enforces this in a Gate
 * callback; this mirrors that decision so the UI cannot disagree with it.
 */
export const hasPermission = (permissionName: string) => {
  const session = getSession();
  if (session.hasAllPermissions) return true;
  // "a|b" means any one of them, the same form the API's route guards use;
  // "a&b" means all of them, for a page that needs two rights to be any use.
  return permissionName
    .split("|")
    .some((option) => option.split("&").every((name) => session.permissions.includes(name.trim())));
};

export const hasAnyPermission = (permissionNames: string[] = []) => {
  const session = getSession();
  if (session.hasAllPermissions) return true;
  return permissionNames.some((name) => session.permissions.includes(name));
};

export const hasRole = (roleName: string) => {
  const session = getSession();
  return session.roles.includes(roleName);
};

/*
 * What a menu entry needs before it is shown.
 *
 * An entry's own `permission` wins; without one it inherits the permission its
 * route demands, so the menu can never offer a link the router would turn away
 * with a 403 page.
 */
let routePermissions: Map<string, string> | null = null;

const permissionForRoute = (name?: string) => {
  if (!name) return undefined;
  if (!routePermissions) {
    routePermissions = new Map();
    for (const r of allRoute as Array<{ name?: string; meta?: { permission?: string } }>) {
      if (r.name && r.meta?.permission) routePermissions.set(r.name, r.meta.permission);
    }
  }
  return routePermissions.get(name);
};

export const canSeeMenuItem = (item: { permission?: string; route?: { name?: string } }) => {
  const needed = item.permission ?? permissionForRoute(item.route?.name);
  return !needed || hasPermission(needed);
};
